import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useIncrementalList } from "@/hooks/use-incremental-list";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useLearnerContext } from "@/lib/learner-context";
import { supabase } from "@/integrations/supabase/client";
import { signedUrl } from "@/lib/uploads";
import { useServerFn } from "@tanstack/react-start";
import { summarizeLibraryResource } from "@/lib/library-ai.functions";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { AICallout } from "@/components/makai/primitives";
import {
  Book, FileText, Video, Headphones, Sparkles, Link as LinkIcon, GraduationCap,
  Search, Bookmark, BookmarkCheck, Download, Play, Star, ExternalLink, X,
  StickyNote, Wand2, ChevronLeft, ChevronRight, Library, ClipboardList, Film, ScrollText,
  Loader2, SlidersHorizontal, Pencil, Trash2,
} from "lucide-react";

export const Route = createFileRoute("/learner/library")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "Library — MAKAI" }] }),
  component: LibraryPage,
});

type Kind =
  | "book" | "notes" | "video" | "audio" | "past_paper" | "interactive" | "link"
  | "teacher_resource" | "assignment" | "recorded_lesson" | "curriculum";

type Resource = {
  id: string;
  title: string;
  description: string | null;
  author: string | null;
  kind: Kind;
  topic: string | null;
  tags: string[] | null;
  url: string | null;
  storage_path: string | null;
  thumbnail_url: string | null;
  size_bytes: number | null;
  pages: number | null;
  duration_seconds: number | null;
  language: string | null;
  is_featured: boolean;
  view_count: number;
  subject_id: string | null;
  class_id: string | null;
  past_paper_id: string | null;
  subject?: { id: string; name: string } | null;
  class?: { id: string; name: string; grade: string | null } | null;
};


type Progress = {
  resource_id: string; percent: number; position_seconds: number | null;
  page: number | null; updated_at: string; completed_at: string | null;
};

type Note = { id: string; body: string; page: number | null; position_seconds: number | null; created_at: string };

const KIND_META: Record<Kind, { label: string; icon: typeof Book; tint: string }> = {
  book:             { label: "Books",             icon: Book,          tint: "from-amber-500/30 to-orange-700/40" },
  video:            { label: "Videos",            icon: Video,         tint: "from-rose-500/30 to-red-800/40" },
  past_paper:       { label: "Past Papers",       icon: GraduationCap, tint: "from-emerald-500/30 to-emerald-800/40" },
  teacher_resource: { label: "Teacher Resources", icon: Library,       tint: "from-sky-500/30 to-blue-900/40" },
  assignment:       { label: "Assignments",       icon: ClipboardList, tint: "from-violet-500/30 to-purple-900/40" },
  recorded_lesson:  { label: "Recorded Lessons",  icon: Film,          tint: "from-pink-500/30 to-fuchsia-900/40" },
  curriculum:       { label: "Curriculum",        icon: ScrollText,    tint: "from-yellow-500/30 to-amber-800/40" },
  audio:            { label: "Audio Lessons",     icon: Headphones,    tint: "from-cyan-500/30 to-teal-800/40" },
  notes:            { label: "Notes",             icon: FileText,      tint: "from-indigo-500/30 to-indigo-900/40" },
  interactive:      { label: "Interactive",       icon: Sparkles,      tint: "from-lime-500/30 to-green-800/40" },
  link:             { label: "Web Links",         icon: LinkIcon,      tint: "from-slate-500/30 to-slate-800/40" },
};

const ROW_ORDER: Kind[] = [
  "book", "video", "past_paper", "teacher_resource", "assignment",
  "recorded_lesson", "curriculum", "audio", "notes", "interactive", "link",
];

function formatBytes(b: number | null) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
function formatDuration(s: number | null) {
  if (!s) return "";
  const m = Math.floor(s / 60); const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

function LibraryPage() {
  const { ctx } = useLearnerContext();
  const [resources, setResources] = useState<Resource[]>([]);
  const [bookmarks, setBookmarks] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<Record<string, Progress>>({});
  const [recent, setRecent] = useState<Resource[]>([]);
  const [subjects, setSubjects] = useState<{ id: string; name: string }[]>([]);
  const [classes, setClasses] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  // search & filters
  const [q, setQ] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    subjectId: "", classId: "", topic: "", kind: "" as Kind | "",
    language: "", featuredOnly: false, year: "", section: "" as "" | "A" | "B",
  });

  // detail modal
  const [active, setActive] = useState<Resource | null>(null);

  useEffect(() => {
    if (!ctx) return;
    (async () => {
      setLoading(true);
      const schoolFilter = ctx.schoolId
        ? `school_id.eq.${ctx.schoolId},visibility.eq.public`
        : `visibility.eq.public`;
      const { data: res } = await supabase
        .from("library_resources")
        .select("id,title,description,author,kind,topic,tags,url,storage_path,thumbnail_url,size_bytes,pages,duration_seconds,language,is_featured,view_count,subject_id,class_id,past_paper_id,subject:subjects_catalog(id,name),class:classes(id,name,grade)")
        .or(schoolFilter)
        .order("is_featured", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(500);
      setResources((res as Resource[] | null) ?? []);

      const { data: bms } = await supabase.from("library_bookmarks").select("resource_id");
      setBookmarks(new Set((bms ?? []).map((b: { resource_id: string }) => b.resource_id)));

      const { data: prog } = await supabase
        .from("library_progress")
        .select("resource_id,percent,position_seconds,page,updated_at,completed_at")
        .order("updated_at", { ascending: false });
      const pmap: Record<string, Progress> = {};
      for (const p of (prog as Progress[]) ?? []) pmap[p.resource_id] = p;
      setProgress(pmap);

      const { data: views } = await supabase
        .from("library_views")
        .select("viewed_at,resource:library_resources(id,title,description,author,kind,topic,tags,url,storage_path,thumbnail_url,size_bytes,pages,duration_seconds,language,is_featured,view_count,subject_id,class_id,past_paper_id,subject:subjects_catalog(id,name),class:classes(id,name,grade))")
        .order("viewed_at", { ascending: false }).limit(20);
      const seen = new Set<string>();
      const recentList: Resource[] = [];
      for (const v of (views as { resource: Resource | null }[]) ?? []) {
        if (v.resource && !seen.has(v.resource.id)) { seen.add(v.resource.id); recentList.push(v.resource); }
      }
      setRecent(recentList);

      if (ctx.schoolId) {
        const { data: subs } = await supabase.from("subjects_catalog").select("id,name").eq("school_id", ctx.schoolId).order("name");
        setSubjects(subs ?? []);
        const { data: cls } = await supabase.from("classes").select("id,name").eq("school_id", ctx.schoolId).order("name");
        setClasses(cls ?? []);
      }
      setLoading(false);
    })();
  }, [ctx]);

  const searchActive = q.trim().length > 0 || Object.values(filters).some((v) => v !== "" && v !== false);

  const extractYear = (r: Resource): string | null => {
    const tagYear = (r.tags ?? []).find((t) => /^(19|20)\d{2}$/.test(t));
    if (tagYear) return tagYear;
    const m = `${r.title} ${r.description ?? ""}`.match(/\b(19|20)\d{2}\b/);
    return m ? m[0] : null;
  };
  const extractSection = (r: Resource): "A" | "B" | null => {
    const hay = `${(r.tags ?? []).join(" ")} ${r.title} ${r.description ?? ""}`.toLowerCase();
    if (/section[\s_-]*a\b/.test(hay)) return "A";
    if (/section[\s_-]*b\b/.test(hay)) return "B";
    return null;
  };

  const availableYears = useMemo(() => {
    const set = new Set<string>();
    for (const r of resources) { const y = extractYear(r); if (y) set.add(y); }
    return Array.from(set).sort((a, b) => Number(b) - Number(a));
  }, [resources]);

  const filteredAll = useMemo(() => {
    return resources.filter((r) => {
      if (filters.kind && r.kind !== filters.kind) return false;
      if (filters.subjectId && r.subject_id !== filters.subjectId) return false;
      if (filters.classId && r.class_id !== filters.classId) return false;
      if (filters.topic && r.topic !== filters.topic) return false;
      if (filters.language && r.language !== filters.language) return false;
      if (filters.featuredOnly && !r.is_featured) return false;
      if (filters.year && extractYear(r) !== filters.year) return false;
      if (filters.section && extractSection(r) !== filters.section) return false;
      if (q) {
        const s = q.toLowerCase();
        const hay = `${r.title} ${r.description ?? ""} ${r.author ?? ""} ${r.topic ?? ""} ${(r.tags ?? []).join(" ")}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [resources, q, filters]);

  const continueRow = useMemo(() => {
    return Object.values(progress)
      .filter((p) => (p.percent ?? 0) < 95)
      .sort((a, b) => +new Date(b.updated_at) - +new Date(a.updated_at))
      .map((p) => resources.find((r) => r.id === p.resource_id))
      .filter((r): r is Resource => !!r)
      .slice(0, 12);
  }, [progress, resources]);

  const featured = useMemo(() => resources.filter((r) => r.is_featured).slice(0, 6), [resources]);

  const recommended = useMemo(() => {
    return [...resources]
      .map((r) => {
        let score = 0;
        if (r.is_featured) score += 3;
        if (ctx?.classId && r.class_id === ctx.classId) score += 4;
        if (progress[r.id]) score -= 4;
        score += Math.min(2, r.view_count / 50);
        return { r, score };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 14)
      .map((x) => x.r);
  }, [resources, ctx, progress]);

  const byKind = useMemo(() => {
    const map: Record<Kind, Resource[]> = {} as Record<Kind, Resource[]>;
    for (const k of ROW_ORDER) map[k] = [];
    for (const r of resources) {
      if (map[r.kind]) map[r.kind].push(r);
    }
    return map;
  }, [resources]);

  const bookmarkRow = useMemo(() => resources.filter((r) => bookmarks.has(r.id)).slice(0, 18), [resources, bookmarks]);

  return (
    <AppShell role="learner" title="Library" subtitle="Books, videos, past papers and more — your Netflix for learning.">
      <div className="-mx-3 sm:-mx-4 lg:-mx-6 -mt-2 space-y-8 pb-12">
        {/* Search bar */}
        <div className="sticky top-0 z-30 -mt-2 bg-gradient-to-b from-background via-background to-background/80 px-3 pb-3 pt-3 backdrop-blur sm:px-4 lg:px-6">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search books, videos, past papers, topics, authors…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="h-11 rounded-full border-border/50 bg-card/80 pl-10 text-sm shadow-soft"
              />
            </div>
            <Button
              variant={showFilters ? "default" : "outline"}
              size="icon"
              className="h-11 w-11 shrink-0 rounded-full"
              onClick={() => setShowFilters((s) => !s)}
              aria-label="Filters"
            >
              <SlidersHorizontal className="h-4 w-4" />
            </Button>
          </div>
          {showFilters && (
            <div className="mt-3 grid gap-2 rounded-2xl border border-border bg-card/80 p-3 shadow-soft sm:grid-cols-2 lg:grid-cols-6">
              <select value={filters.kind} onChange={(e) => setFilters({ ...filters, kind: e.target.value as Kind | "" })}
                className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
                <option value="">All types</option>
                {ROW_ORDER.map((k) => <option key={k} value={k}>{KIND_META[k].label}</option>)}
              </select>
              <select value={filters.subjectId} onChange={(e) => setFilters({ ...filters, subjectId: e.target.value })}
                className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
                <option value="">All subjects</option>
                {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <select value={filters.classId} onChange={(e) => setFilters({ ...filters, classId: e.target.value })}
                className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
                <option value="">All classes</option>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <Input placeholder="Topic" value={filters.topic} onChange={(e) => setFilters({ ...filters, topic: e.target.value })} className="h-10" />
              <select value={filters.language} onChange={(e) => setFilters({ ...filters, language: e.target.value })}
                className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
                <option value="">Any language</option>
                <option value="en">English</option>
                <option value="lg">Luganda</option>
                <option value="sw">Swahili</option>
              </select>
              <select value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
                <option value="">Any year</option>
                {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <select value={filters.section} onChange={(e) => setFilters({ ...filters, section: e.target.value as "" | "A" | "B" })}
                className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
                <option value="">Any section</option>
                <option value="A">Section A</option>
                <option value="B">Section B</option>
              </select>
              <label className="flex items-center gap-2 rounded-lg border border-input bg-background px-3 py-2 text-sm">
                <input type="checkbox" checked={filters.featuredOnly} onChange={(e) => setFilters({ ...filters, featuredOnly: e.target.checked })} />
                Featured only
              </label>
              {searchActive && (
                <Button variant="ghost" size="sm" className="col-span-full justify-self-start"
                  onClick={() => { setQ(""); setFilters({ subjectId: "", classId: "", topic: "", kind: "", language: "", featuredOnly: false, year: "", section: "" }); }}>
                  Clear all
                </Button>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="px-3 sm:px-4 lg:px-6">
            <div className="h-72 animate-pulse rounded-3xl bg-card/60" />
          </div>
        ) : searchActive ? (
          <SearchResults items={filteredAll} onOpen={setActive} bookmarks={bookmarks} progress={progress} />
        ) : (
          <>
            {featured.length > 0 && <Hero items={featured} onOpen={setActive} progress={progress} />}
            {continueRow.length > 0 && (
              <Row title="Continue learning" icon={Play} items={continueRow} onOpen={setActive} bookmarks={bookmarks} progress={progress} />
            )}
            {recommended.length > 0 && (
              <Row title="Recommended for you" icon={Sparkles} items={recommended} onOpen={setActive} bookmarks={bookmarks} progress={progress} />
            )}
            {bookmarkRow.length > 0 && (
              <Row title="Your bookmarks" icon={BookmarkCheck} items={bookmarkRow} onOpen={setActive} bookmarks={bookmarks} progress={progress} />
            )}
            {recent.length > 0 && (
              <Row title="Recently viewed" icon={Library} items={recent} onOpen={setActive} bookmarks={bookmarks} progress={progress} />
            )}
            {ROW_ORDER.map((k) => byKind[k].length > 0 && (
              <Row key={k} title={KIND_META[k].label} icon={KIND_META[k].icon} items={byKind[k]} onOpen={setActive} bookmarks={bookmarks} progress={progress} />
            ))}
            {resources.length === 0 && (
              <div className="mx-3 rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground sm:mx-4 lg:mx-6">
                No resources yet. Ask your school to publish materials, or browse public resources later.
              </div>
            )}
          </>
        )}

        <div className="px-3 sm:px-4 lg:px-6">
          <AICallout
            description="I can summarise any resource, recommend reads and find past papers."
            suggestions={[
              "Recommend related books",
              "Find a past paper for this topic",
              "What should I read next?",
              "Summarise the resource I just opened",
            ]}
          />
        </div>
      </div>

      {active && (
        <DetailModal
          resource={active}
          onClose={() => setActive(null)}
          bookmarked={bookmarks.has(active.id)}
          progress={progress[active.id]}
          onBookmarkChange={(b) => {
            const next = new Set(bookmarks);
            if (b) next.add(active.id); else next.delete(active.id);
            setBookmarks(next);
          }}
          onProgressChange={(p) => setProgress((prev) => ({ ...prev, [active.id]: p }))}
        />
      )}
    </AppShell>
  );
}

/* ---------- Hero ---------- */
function Hero({ items, onOpen, progress }: { items: Resource[]; onOpen: (r: Resource) => void; progress: Record<string, Progress> }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 7000);
    return () => clearInterval(t);
  }, [items.length]);
  const r = items[idx];
  const meta = KIND_META[r.kind];
  const p = progress[r.id]?.percent ?? 0;
  return (
    <div className="px-3 sm:px-4 lg:px-6">
      <div className={cn("relative h-72 overflow-hidden rounded-3xl border border-border bg-gradient-to-br shadow-lg sm:h-80", meta.tint)}>
        {r.thumbnail_url && (
          <img loading="lazy" decoding="async" src={r.thumbnail_url} alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/60 to-transparent" />
        <div className="relative flex h-full flex-col justify-end gap-3 p-6 sm:p-8">
          <div className="inline-flex w-fit items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary">
            <Star className="h-3 w-3 fill-current" /> Featured · {meta.label}
          </div>
          <h2 className="max-w-xl text-2xl font-extrabold leading-tight sm:text-3xl">{r.title}</h2>
          {r.description && <p className="max-w-xl text-sm text-foreground/80 line-clamp-2">{r.description}</p>}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button onClick={() => onOpen(r)} className="rounded-full"><Play className="mr-1.5 h-4 w-4 fill-current" /> {p > 0 ? "Resume" : "Open"}</Button>
            <Button variant="outline" onClick={() => onOpen(r)} className="rounded-full">More info</Button>
          </div>
        </div>
        {items.length > 1 && (
          <div className="absolute bottom-3 right-4 flex gap-1.5">
            {items.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)}
                className={cn("h-1.5 rounded-full transition-all", i === idx ? "w-6 bg-primary" : "w-1.5 bg-foreground/30")} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Row ---------- */
function Row({
  title, icon: Icon, items, onOpen, bookmarks, progress,
}: {
  title: string; icon: typeof Book; items: Resource[];
  onOpen: (r: Resource) => void; bookmarks: Set<string>; progress: Record<string, Progress>;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  function scroll(dir: 1 | -1) {
    ref.current?.scrollBy({ left: dir * (ref.current.clientWidth * 0.85), behavior: "smooth" });
  }
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2 px-3 sm:px-4 lg:px-6">
        <h3 className="flex items-center gap-2 text-lg font-bold">
          <Icon className="h-5 w-5 text-primary" /> {title}
        </h3>
        <div className="hidden gap-1 sm:flex">
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => scroll(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => scroll(1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div
        ref={ref}
        className="flex snap-x snap-mandatory gap-3 overflow-x-auto px-3 pb-2 sm:px-4 lg:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((r) => (
          <Card key={r.id} r={r} onOpen={onOpen} bookmarked={bookmarks.has(r.id)} progress={progress[r.id]} />
        ))}
      </div>
    </section>
  );
}

/* ---------- Card ---------- */
function Card({ r, onOpen, bookmarked, progress }: { r: Resource; onOpen: (r: Resource) => void; bookmarked: boolean; progress?: Progress }) {
  const meta = KIND_META[r.kind];
  const Icon = meta.icon;
  const p = progress?.percent ?? 0;
  return (
    <button
      onClick={() => onOpen(r)}
      className="group relative w-44 shrink-0 snap-start overflow-hidden rounded-2xl border border-border bg-card text-left shadow-soft transition hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-lg sm:w-52"
    >
      <div className={cn("relative aspect-[2/3] w-full overflow-hidden bg-gradient-to-br", meta.tint)}>
        {r.thumbnail_url ? (
          <img src={r.thumbnail_url} alt="" className="h-full w-full object-cover transition group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="grid h-full place-items-center text-foreground/70">
            <Icon className="h-12 w-12" />
          </div>
        )}
        <div className="absolute inset-x-0 top-0 flex items-start justify-between p-2">
          <span className="rounded-full bg-background/80 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider backdrop-blur">{meta.label}</span>
          {bookmarked && <BookmarkCheck className="h-4 w-4 fill-primary text-primary drop-shadow" />}
        </div>
        {r.is_featured && (
          <div className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground">
            <Star className="h-3 w-3 fill-current" /> Featured
          </div>
        )}
        {p > 0 && (
          <div className="absolute inset-x-0 bottom-0 h-1 bg-foreground/20">
            <div className="h-full bg-primary" style={{ width: `${Math.min(100, p)}%` }} />
          </div>
        )}
      </div>
      <div className="space-y-1 p-2.5">
        <h4 className="line-clamp-2 text-sm font-semibold leading-tight">{r.title}</h4>
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          {r.subject?.name && <span className="truncate">{r.subject.name}</span>}
          {r.pages && <span>· {r.pages}p</span>}
          {r.duration_seconds && <span>· {formatDuration(r.duration_seconds)}</span>}
        </div>
      </div>
    </button>
  );
}

/* ---------- Search results ---------- */
function SearchResults({ items, onOpen, bookmarks, progress }: { items: Resource[]; onOpen: (r: Resource) => void; bookmarks: Set<string>; progress: Record<string, Progress> }) {
  const { visible, hasMore, sentinelRef } = useIncrementalList(items, 30);
  return (
    <div className="px-3 sm:px-4 lg:px-6">
      <h3 className="mb-3 text-sm font-semibold text-muted-foreground">{items.length} result{items.length === 1 ? "" : "s"}</h3>
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No resources match your search.</div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {visible.map((r: Resource) => <Card key={r.id} r={r} onOpen={onOpen} bookmarked={bookmarks.has(r.id)} progress={progress[r.id]} />)}
          </div>
          {hasMore && (
            <div ref={sentinelRef} className="py-6 text-center text-xs text-muted-foreground" aria-live="polite">
              Loading more resources…
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ---------- Detail modal ---------- */
function DetailModal({
  resource: r, onClose, bookmarked, progress, onBookmarkChange, onProgressChange,
}: {
  resource: Resource; onClose: () => void; bookmarked: boolean; progress?: Progress;
  onBookmarkChange: (b: boolean) => void; onProgressChange: (p: Progress) => void;
}) {
  const meta = KIND_META[r.kind];
  const Icon = meta.icon;
  const summarize = useServerFn(summarizeLibraryResource);
  const [summary, setSummary] = useState<{ summary: string; key_points: string[] } | null>(null);
  const [summarizing, setSummarizing] = useState(false);
  const [notes, setNotes] = useState<Note[]>([]);
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const navigate = useNavigate();


  useEffect(() => {
    (async () => {
      const { data: s } = await supabase.from("library_summaries").select("summary,key_points").eq("resource_id", r.id).maybeSingle();
      if (s) setSummary({ summary: s.summary, key_points: (s.key_points as string[] | null) ?? [] });
      const { data: n } = await supabase.from("library_notes").select("id,body,page,position_seconds,created_at").eq("resource_id", r.id).order("created_at", { ascending: false });
      setNotes((n as Note[] | null) ?? []);
    })();
  }, [r.id]);

  async function open() {
    // Interactive past paper: launch the quiz-style player instead of opening a PDF.
    if (r.past_paper_id) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from("library_views").insert({ user_id: user.id, resource_id: r.id });
      }
      onClose();
      navigate({ to: "/learner/papers/$paperId", params: { paperId: r.past_paper_id } });
      return;
    }

    // Prefer cached offline blob when available — works fully offline.
    try {
      const { getOfflineBlobUrl } = await import("@/lib/offline/downloads");
      const blobUrl = await getOfflineBlobUrl(r.id);
      if (blobUrl) {
        window.open(blobUrl, "_blank", "noopener,noreferrer");
        // Still record the view if online.
        if (navigator.onLine) {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            await supabase.from("library_views").insert({ user_id: user.id, resource_id: r.id });
          }
        }
        return;
      }
    } catch { /* fall through to network */ }

    if (!navigator.onLine) {
      toast.error("You're offline. Save this resource first to view it later.");
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase.from("library_views").insert({ user_id: user.id, resource_id: r.id });
      await supabase.from("library_resources").update({ view_count: r.view_count + 1 }).eq("id", r.id);
      const pct = Math.max(progress?.percent ?? 0, 5);
      const { data: prow } = await supabase.from("library_progress").upsert(
        { user_id: user.id, resource_id: r.id, percent: pct },
        { onConflict: "user_id,resource_id" },
      ).select("resource_id,percent,position_seconds,page,updated_at,completed_at").single();
      if (prow) onProgressChange(prow as Progress);
    }
    let url = r.url;
    if (r.storage_path) url = await signedUrl("library-files", r.storage_path, 3600);
    if (!url) { toast.error("No file available"); return; }
    window.open(url, "_blank", "noopener,noreferrer");
  }

  async function download() {
    let url = r.url;
    if (r.storage_path) url = await signedUrl("library-files", r.storage_path, 3600);
    if (!url) { toast.error("No file"); return; }
    try {
      const { saveForOffline } = await import("@/lib/offline/downloads");
      await saveForOffline({
        id: r.id,
        kind: (r.kind as "book" | "pdf" | "video" | "paper" | "resource") ?? "resource",
        title: r.title,
        url,
        updatedAt: (r as { updated_at?: string | null }).updated_at ?? null,
      });
      toast.success("Saved for offline use");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save offline");
    }
  }

  async function toggleBookmark() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    if (bookmarked) {
      await supabase.from("library_bookmarks").delete().eq("user_id", user.id).eq("resource_id", r.id);
    } else {
      await supabase.from("library_bookmarks").insert({ user_id: user.id, resource_id: r.id });
    }
    onBookmarkChange(!bookmarked);
  }

  async function generateSummary() {
    setSummarizing(true);
    try {
      const s = await summarize({ data: { resourceId: r.id, force: !!summary } });
      setSummary({ summary: s.summary, key_points: (s.key_points as string[] | null) ?? [] });
    } catch (e) {
      toast.error((e as Error).message || "Could not generate summary");
    } finally { setSummarizing(false); }
  }

  async function saveNote() {
    if (!draft.trim()) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    if (editingId) {
      const { data } = await supabase.from("library_notes").update({ body: draft }).eq("id", editingId).select("id,body,page,position_seconds,created_at").single();
      if (data) setNotes((n) => n.map((x) => x.id === editingId ? data as Note : x));
      setEditingId(null);
    } else {
      const { data } = await supabase.from("library_notes").insert({ user_id: user.id, resource_id: r.id, body: draft }).select("id,body,page,position_seconds,created_at").single();
      if (data) setNotes((n) => [data as Note, ...n]);
    }
    setDraft("");
    toast.success("Note saved");
  }

  async function deleteNote(id: string) {
    await supabase.from("library_notes").delete().eq("id", id);
    setNotes((n) => n.filter((x) => x.id !== id));
  }

  const pct = progress?.percent ?? 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] w-[min(100%,920px)] overflow-hidden p-0 sm:rounded-3xl">
        <div className={cn("relative h-44 overflow-hidden bg-gradient-to-br sm:h-56", meta.tint)}>
          {r.thumbnail_url && <img loading="lazy" decoding="async" src={r.thumbnail_url} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
          <button onClick={onClose} className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-background/80 backdrop-blur hover:bg-background">
            <X className="h-4 w-4" />
          </button>
          <div className="absolute inset-x-0 bottom-0 p-5">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-background/80 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider backdrop-blur">
              <Icon className="h-3 w-3" /> {meta.label}
            </div>
            <DialogHeader>
              <DialogTitle className="mt-2 text-xl font-extrabold sm:text-2xl">{r.title}</DialogTitle>
            </DialogHeader>
            {r.author && <div className="text-xs text-muted-foreground">by {r.author}</div>}
          </div>
        </div>

        <div className="max-h-[calc(92vh-14rem)] overflow-y-auto px-5 pb-6 pt-3 sm:px-7">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {r.subject?.name && <span>{r.subject.name}</span>}
            {r.class?.name && <span>· {r.class.name}</span>}
            {r.topic && <span>· {r.topic}</span>}
            {r.pages && <span>· {r.pages} pages</span>}
            {r.duration_seconds && <span>· {formatDuration(r.duration_seconds)}</span>}
            {r.size_bytes && <span>· {formatBytes(r.size_bytes)}</span>}
            {r.language && <span>· {r.language.toUpperCase()}</span>}
          </div>

          {pct > 0 && (
            <div className="mt-3 flex items-center gap-2 text-xs">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                <div className="h-full bg-primary" style={{ width: `${Math.min(100, pct)}%` }} />
              </div>
              <span className="text-muted-foreground">{Math.round(pct)}%</span>
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button onClick={open} className="rounded-full">
              <Play className="mr-1.5 h-4 w-4 fill-current" /> {pct > 0 ? "Resume" : "Open"}
            </Button>
            <Button variant="outline" onClick={download} className="rounded-full">
              <Download className="mr-1.5 h-4 w-4" /> Save offline
            </Button>
            <Button variant="outline" onClick={toggleBookmark} className="rounded-full">
              {bookmarked ? <BookmarkCheck className="mr-1.5 h-4 w-4 text-primary" /> : <Bookmark className="mr-1.5 h-4 w-4" />}
              {bookmarked ? "Bookmarked" : "Bookmark"}
            </Button>
            {r.url && (
              <a href={r.url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center rounded-full border border-input bg-background px-3 py-1.5 text-sm hover:bg-muted">
                <ExternalLink className="mr-1.5 h-4 w-4" /> Source
              </a>
            )}
          </div>

          {r.description && <p className="mt-4 text-sm text-foreground/85">{r.description}</p>}

          {/* AI summary */}
          <div className="mt-6 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-4">
            <div className="flex items-center justify-between gap-2">
              <h4 className="flex items-center gap-1.5 text-sm font-bold"><Wand2 className="h-4 w-4 text-primary" /> AI Summary</h4>
              <Button size="sm" variant="ghost" onClick={generateSummary} disabled={summarizing}>
                {summarizing ? <Loader2 className="h-4 w-4 animate-spin" /> : summary ? "Regenerate" : "Generate"}
              </Button>
            </div>
            {summary ? (
              <div className="mt-2 space-y-2">
                <p className="text-sm leading-relaxed">{summary.summary}</p>
                {summary.key_points?.length > 0 && (
                  <ul className="ml-4 list-disc space-y-1 text-sm text-foreground/85">
                    {summary.key_points.map((k, i) => <li key={i}>{k}</li>)}
                  </ul>
                )}
              </div>
            ) : (
              <p className="mt-2 text-xs text-muted-foreground">Tap “Generate” for an AI overview and key points from MAKAI.</p>
            )}
          </div>

          {/* Notes */}
          <div className="mt-6 space-y-3">
            <h4 className="flex items-center gap-1.5 text-sm font-bold"><StickyNote className="h-4 w-4 text-primary" /> My Notes</h4>
            <div className="space-y-2">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Jot a quick note…"
                className="min-h-[80px] rounded-xl"
              />
              <div className="flex gap-2">
                <Button onClick={saveNote} disabled={!draft.trim()} size="sm">
                  {editingId ? "Update note" : "Save note"}
                </Button>
                {editingId && (
                  <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setDraft(""); }}>Cancel</Button>
                )}
              </div>
            </div>
            {notes.length === 0 ? (
              <p className="text-xs text-muted-foreground">No notes yet.</p>
            ) : (
              <ul className="space-y-2">
                {notes.map((n) => (
                  <li key={n.id} className="rounded-xl border border-border bg-card p-3">
                    <div className="whitespace-pre-wrap text-sm">{n.body}</div>
                    <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
                      <span>{new Date(n.created_at).toLocaleString()}</span>
                      <div className="flex gap-1">
                        <button onClick={() => { setEditingId(n.id); setDraft(n.body); }} className="rounded-full p-1 hover:bg-muted"><Pencil className="h-3 w-3" /></button>
                        <button onClick={() => deleteNote(n.id)} className="rounded-full p-1 hover:bg-muted"><Trash2 className="h-3 w-3" /></button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
